import React, { useState, useMemo, useEffect } from 'react';
import styled from 'styled-components';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useCommunityDrawer } from '@/features/community/drawerStore';
import { colors } from '@/constants/colors';
import { toast } from 'react-toastify';
import Input from '../UI/Input';
import Btn from '../UI/Btn';
import FileUploader from '../UI/FileUploader';
import { useUIStore } from '@/store/useUIStore';
import {
   listPartners,
   createInquiry,
   getPartnerPaymentOptions,
   getMyResellerApi,
} from '@/api/vendo';
import { useDistributorStatus } from '@/hooks/useDistributorStatus';
import { PayLogo } from '../PaymentLogos';

// Optional pre-selection hint — Partners.tsx still passes a mock partner
// shape today. We ignore the legacy fields and only consume `id` (matched
// against real partner _id, fallback to picker if no match).
interface PartnerHint {
   id?: string;
   /**
    * Quantity already chosen on the seller page's checkout panel. Opening
    * the form with the box reset to 1 would silently throw that choice
    * away, so the panel hands it over.
    */
   credits?: number;
}

interface Props {
   data?: PartnerHint;
}

const InquireCredits = ({ data }: Props) => {
   const { handleClose } = useUIStore();
   const queryClient = useQueryClient();
   const navigate = useNavigate();

   const { data: partners = [], isLoading: partnersLoading } = useQuery({
      queryKey: ['vendo', 'partners'],
      queryFn: listPartners,
   });
   // A distributor buying from a co-distributor is wholesale — earns no
   // reseller points (enforced server-side). Warn them up front.
   const { isDistributor } = useDistributorStatus();

   const [partnerId, setPartnerId] = useState<string>('');
   const selectedPartner = useMemo(
      () => partners.find((p) => p._id === partnerId),
      [partners, partnerId]
   );
   // First-time auto-select: prefer hint match, else first partner in list.
   useEffect(() => {
      if (partnerId || partners.length === 0) return;
      const hintMatch = data?.id
         ? partners.find((p) => p._id === data.id)
         : undefined;
      setPartnerId((hintMatch ?? partners[0])._id);
   }, [partnerId, partners, data?.id]);

   const [credits, setCredits] = useState(
      data?.credits && data.credits > 0 ? String(data.credits) : '1'
   );
   const [note, setNote] = useState('');
   const [proofUrl, setProofUrl] = useState('');
   const [error, setError] = useState(false);
   const [isLoading, setIsLoading] = useState(false);

   // The viewer's reseller record (if any) — used to decide which system price
   // applies to the selected distributor.
   const { data: myReseller } = useQuery({
      queryKey: ['vendo', 'reseller', 'me'],
      queryFn: getMyResellerApi,
   });

   // System-controlled price for the selected distributor: an approved reseller
   // of THIS distributor pays the reseller price; everyone else pays the base.
   const priceForPartner = (p?: (typeof partners)[number]): number => {
      if (!p) return 0;
      const base = p.baseCreditPrice ?? p.defaultPricePerCredit ?? 0;
      const reseller = p.resellerPricePerCredit ?? base;
      const r = myReseller?.reseller;
      const isApprovedResellerHere =
         r?.status === 'approved' && r?.distributor === p._id;
      return isApprovedResellerHere ? reseller : base;
   };

   const creditsNum = Number(credits) || 0;
   const pricePerCredit = priceForPartner(selectedPartner);
   const isResellerPrice =
      myReseller?.reseller?.status === 'approved' &&
      myReseller?.reseller?.distributor === selectedPartner?._id;
   const total = creditsNum * pricePerCredit;

   const {
      data: paymentOptions,
      error: paymentError,
      isLoading: paymentLoading,
   } = useQuery({
      queryKey: ['vendo', 'partner-payment-options', partnerId],
      queryFn: () => getPartnerPaymentOptions(partnerId),
      enabled: !!partnerId,
      retry: false,
   });

   const totalAccounts =
      (paymentOptions?.bankAccounts.length ?? 0) +
      (paymentOptions?.gcashAccounts.length ?? 0);

   // A helper or SmartFi support viewing someone else's account can never
   // buy credits for them (team-access rule, 403 TEAM_DENIED). Say so —
   // that is not "the distributor has no payment options".
   const paymentDenied =
      !!paymentError &&
      (paymentError as { response?: { data?: { code?: string } } }).response
         ?.data?.code === 'TEAM_DENIED';
   const paymentFailed = !!paymentError && !paymentDenied;
   const noPaymentOptions =
      !!selectedPartner && !paymentError && !paymentLoading && totalAccounts === 0;
   const paymentNote = paymentDenied
      ? "You're viewing this account as a helper or SmartFi support — only the account owner can request credits. Switch back to your own account first."
      : paymentFailed
      ? "Couldn't load this distributor's payment details — close this and try again."
      : noPaymentOptions
      ? "This distributor hasn't added payment options yet — you can't submit until they do. Reach out via the contact info above."
      : null;
   const proofMissing = !proofUrl.trim();
   const partnerAvailable = selectedPartner?.availableCredits ?? 0;
   const overLimit = !!selectedPartner && creditsNum > partnerAvailable;

   const blockReason = !selectedPartner
      ? 'Pick a distributor first'
      : partnerAvailable === 0
      ? 'This distributor has no credits available right now — check back later'
      : creditsNum <= 0
      ? 'Enter a valid number of credits'
      : overLimit
      ? `Only ${partnerAvailable.toLocaleString()} credit${
           partnerAvailable === 1 ? '' : 's'
        } available from this distributor`
      : paymentDenied
      ? 'Only the account owner can request credits — switch back to your own account'
      : paymentFailed
      ? "Couldn't load the payment details — try again"
      : paymentLoading
      ? 'Loading payment details…'
      : noPaymentOptions
      ? 'This distributor has no payment options yet'
      : proofMissing
      ? 'Upload payment proof to continue'
      : null;
   const canSubmit = !isLoading && !blockReason;

   const handleSubmit = async () => {
      if (!canSubmit || !selectedPartner) return;
      setIsLoading(true);
      try {
         await createInquiry({
            distributorPartnerId: selectedPartner._id,
            requestedCredits: creditsNum,
            notes: note.trim() || undefined,
            paymentProofUrl: proofUrl.trim(),
         });
         queryClient.invalidateQueries({ queryKey: ['vendo', 'inquiries'] });
         // Ray 2026-10-28: say what happens next — the bell tells them when it is approved (inquiry_approved) and when the credits arrive.
         toast.success(
            `Sent to ${selectedPartner.displayName} · ${creditsNum} credit${creditsNum === 1 ? '' : 's'} · ₱${total.toLocaleString()}. Waiting for their approval — the bell will tell you once they approve.`,
            { autoClose: 7000 }
         );
         handleClose();
         // Land on My Inquiries, where the new one sits "Awaiting distributor" (Ray 2026-10-28); sent from the chat, the chat folds away.
         useCommunityDrawer.getState().setOpen(false);
         navigate('/wifi-partners?tab=my-inquiries');
      } catch (err: any) {
         toast.error(err?.response?.data?.error || 'Failed to send inquiry');
      } finally {
         setIsLoading(false);
      }
   };

   return (
      <Wrapper>
         <ScrollArea>
            <DistributorSummary>
               <SummaryLabel>Requesting from</SummaryLabel>
               {partnersLoading ? (
                  <SummaryName>Loading partners…</SummaryName>
               ) : partners.length === 0 ? (
                  <SummaryName>No active distributors yet</SummaryName>
               ) : (
                  <PartnerSelect
                     value={partnerId}
                     onChange={(e) => setPartnerId(e.target.value)}
                  >
                     {partners.map((p) => (
                        <option key={p._id} value={p._id}>
                           {p.displayName} — {p.city}, {p.region} · ₱
                           {priceForPartner(p)}/credit · {p.tier}
                           {(p.availableCredits ?? 0) === 0 ? ' · no credits' : ''}
                        </option>
                     ))}
                  </PartnerSelect>
               )}
               {selectedPartner && (
                  <SummaryMeta>
                     {selectedPartner.businessName} · ₱{pricePerCredit}/credit
                     {isResellerPrice ? ' (reseller price)' : ''} ·{' '}
                     {selectedPartner.cumulativeCreditsSold.toLocaleString()} sold
                  </SummaryMeta>
               )}
            </DistributorSummary>

            {isDistributor && (
               <CoDistributorNote>
                  ℹ️ You're a distributor — buying from a co-distributor is
                  wholesale and <strong>won't earn you points</strong>.
               </CoDistributorNote>
            )}

            <FieldWrap>
               <Input
                  label="How many credits? *"
                  name="credits"
                  value={credits}
                  placeholder="1"
                  error={(error && creditsNum <= 0) || overLimit}
                  onChange={(e) => {
                     const v = e.target.value.replace(/[^0-9]/g, '');
                     setCredits(v);
                     if (error) setError(false);
                  }}
               />
               {selectedPartner && (
                  <Hint>
                     Available from {selectedPartner.displayName}:{' '}
                     <strong>{partnerAvailable.toLocaleString()}</strong> credit
                     {partnerAvailable === 1 ? '' : 's'}.
                  </Hint>
               )}
               {overLimit && (
                  <FieldError>
                     You can't request more than {partnerAvailable.toLocaleString()}{' '}
                     credit{partnerAvailable === 1 ? '' : 's'} from this distributor.
                  </FieldError>
               )}
            </FieldWrap>

            {selectedPartner &&
               (selectedPartner.contactPhone ||
                  selectedPartner.facebookUrl ||
                  paymentOptions?.contactPhone ||
                  paymentOptions?.facebookUrl) && (
                  <ContactBlock>
                     <ContactTitle>Contact {selectedPartner.displayName}</ContactTitle>
                     <ContactList>
                        {(selectedPartner.contactPhone ||
                           paymentOptions?.contactPhone) && (
                           <ContactRow>
                              <ContactLbl>📞 Phone</ContactLbl>
                              <ContactVal>
                                 {selectedPartner.contactPhone ||
                                    paymentOptions?.contactPhone}
                              </ContactVal>
                           </ContactRow>
                        )}
                        {(selectedPartner.facebookUrl ||
                           paymentOptions?.facebookUrl) && (
                           <ContactRow>
                              <ContactLbl>📘 Facebook</ContactLbl>
                              <ContactLink
                                 href={
                                    selectedPartner.facebookUrl ||
                                    paymentOptions?.facebookUrl
                                 }
                                 target="_blank"
                                 rel="noopener noreferrer"
                              >
                                 {selectedPartner.facebookUrl ||
                                    paymentOptions?.facebookUrl}
                              </ContactLink>
                           </ContactRow>
                        )}
                     </ContactList>
                  </ContactBlock>
               )}

            {selectedPartner && (
               <PaymentOptionsBlock $missing={!!paymentNote}>
                  <PaymentOptionsTitle>
                     Send payment to {selectedPartner.displayName}
                  </PaymentOptionsTitle>
                  {paymentNote ? (
                     <PaymentOptionsEmpty>{paymentNote}</PaymentOptionsEmpty>
                  ) : (
                     <PaymentOptionsList>
                        {paymentOptions?.bankAccounts.map((b, i) => (
                           <PaymentOptionRow key={`bank-${i}`}>
                              <PayLogo kind="bank" size={30} />
                              <PaymentOptionInfo>
                                 <PaymentOptionName>{b.bankName}</PaymentOptionName>
                                 <PaymentOptionMono>{b.accountNumber}</PaymentOptionMono>
                                 <PaymentOptionSub>{b.accountName}</PaymentOptionSub>
                              </PaymentOptionInfo>
                           </PaymentOptionRow>
                        ))}
                        {paymentOptions?.gcashAccounts.map((g, i) => {
                           const maya = (g as { provider?: string }).provider === 'maya';
                           return (
                           <PaymentOptionRow key={`gcash-${i}`}>
                              <PayLogo kind={maya ? 'maya' : 'gcash'} size={30} />
                              <PaymentOptionInfo>
                                 <PaymentOptionName>{maya ? 'Maya' : 'GCash'}</PaymentOptionName>
                                 <PaymentOptionMono>{g.number}</PaymentOptionMono>
                                 <PaymentOptionSub>{g.name}</PaymentOptionSub>
                              </PaymentOptionInfo>
                           </PaymentOptionRow>
                           );
                        })}
                     </PaymentOptionsList>
                  )}
               </PaymentOptionsBlock>
            )}

            <FieldWrap>
               <Label>
                  Payment proof <Required>*</Required>
               </Label>
               <Hint>Screenshot or photo of the payment receipt.</Hint>
               <FileUploader
                  value={proofUrl}
                  onChange={setProofUrl}
                  compact
               />
               {proofMissing && (
                  <FieldError>Payment proof is required.</FieldError>
               )}
            </FieldWrap>

            <FieldWrap>
               <Label>Note (optional)</Label>
               <Textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="How you'd like to pay, deadline, etc."
               />
            </FieldWrap>
         </ScrollArea>

         <Footer>
            <FooterTotals>
               <FooterTotalsRow>
                  <FooterTotalsLbl>
                     {creditsNum} × ₱{pricePerCredit}
                  </FooterTotalsLbl>
                  <FooterTotalsVal>₱{total.toLocaleString()}</FooterTotalsVal>
               </FooterTotalsRow>
               {blockReason && <FooterBlock>{blockReason}</FooterBlock>}
            </FooterTotals>
            <BtnRow>
               <div onClick={handleClose}>
                  <Btn title="Cancel" type="outline" size="small" />
               </div>
               <SendInquiryBtn
                  type="button"
                  disabled={!canSubmit}
                  onClick={handleSubmit}
               >
                  <SendInquiryMain>
                     {isLoading ? 'Sending…' : 'Send Inquiry'}
                  </SendInquiryMain>
                  {!isLoading && creditsNum > 0 && (
                     <SendInquirySub>
                        {creditsNum} credit{creditsNum === 1 ? '' : 's'} · ₱
                        {total.toLocaleString()}
                     </SendInquirySub>
                  )}
               </SendInquiryBtn>
            </BtnRow>
         </Footer>
      </Wrapper>
   );
};

const Wrapper = styled.div`
   width: 520px;
   max-width: 100%;
   height: 390px;
   display: flex;
   flex-direction: column;
   @media (max-width: 768px) { width: 100%; }
`;

const ScrollArea = styled.div`
   flex: 1;
   overflow-y: auto;
   display: flex;
   flex-direction: column;
   gap: 10px;
   padding-right: 4px;
`;

const Footer = styled.div`
   flex-shrink: 0;
   border-top: 1px solid ${colors.GRAY13};
   padding-top: 10px;
   margin-top: 10px;
   display: flex;
   flex-direction: column;
   gap: 8px;
`;

const FooterTotals = styled.div`
   display: flex;
   flex-direction: column;
   gap: 2px;
`;

const FooterTotalsRow = styled.div`
   display: flex;
   justify-content: space-between;
   align-items: center;
`;

const FooterTotalsLbl = styled.span`
   font-size: 12px;
   color: ${colors.GRAY3};
   font-weight: 600;
`;

const FooterTotalsVal = styled.span`
   font-size: 18px;
   font-weight: 800;
   color: ${colors.PRIMARY};
`;

const FooterBlock = styled.p`
   font-size: 11px;
   color: ${colors.DANGER};
   font-weight: 600;
`;

const Required = styled.span`
   color: ${colors.DANGER};
   font-weight: 800;
`;

const FieldError = styled.p`
   font-size: 11px;
   color: ${colors.DANGER};
   font-weight: 600;
`;

const DistributorSummary = styled.div`
   padding: 12px 14px;
   background: var(--app-brand-tint);
   border-radius: 10px;
   display: flex;
   flex-direction: column;
   gap: 3px;
`;

const SummaryLabel = styled.p`
   font-size: 10px;
   font-weight: 700;
   color: ${colors.GRAY5};
   text-transform: uppercase;
   letter-spacing: 0.05em;
`;

const SummaryName = styled.p`
   font-size: 15px;
   font-weight: 800;
   color: ${colors.MAIN_DARK};
`;

const SummaryMeta = styled.p`
   font-size: 12px;
   color: ${colors.GRAY3};
`;

const CoDistributorNote = styled.div`
   padding: 8px 12px;
   background: ${colors.WARNING}14;
   border: 1px solid ${colors.WARNING}40;
   border-radius: 8px;
   font-size: 12px;
   color: ${colors.GRAY_DARK};
   line-height: 1.4;
`;

const PartnerSelect = styled.select`
   width: 100%;
   margin-top: 4px;
   padding: 8px 10px;
   border: 1px solid ${colors.GRAY12};
   border-radius: 6px;
   background: ${colors.WHITE};
   font-size: 13px;
   font-family: inherit;
   color: ${colors.MAIN_DARK};
   font-weight: 700;
   outline: none;
   cursor: pointer;
   &:focus { border-color: ${colors.PRIMARY}; }
`;

const FieldWrap = styled.div`
   display: flex;
   flex-direction: column;
   gap: 4px;
`;

const Hint = styled.p`
   font-size: 11px;
   color: ${colors.GRAY5};
`;

const Label = styled.label`
   font-size: 13px;
   color: ${colors.GRAY1};
   font-weight: 600;
`;

const Textarea = styled.textarea`
   width: 100%;
   padding: 10px 12px;
   border: 1px solid ${colors.GRAY12};
   border-radius: 6px;
   font-size: 13px;
   font-family: inherit;
   color: ${colors.GRAY_DARK};
   background: var(--app-surface);
   outline: none;
   resize: vertical;
   min-height: 60px;
   &:focus { border-color: ${colors.PRIMARY}; }
`;

const ContactBlock = styled.div`
   padding: 10px;
   background: ${colors.GRAY14};
   border: 1px solid ${colors.GRAY12};
   border-radius: 10px;
   display: flex;
   flex-direction: column;
   gap: 6px;
`;

const ContactTitle = styled.p`
   font-size: 12px;
   font-weight: 800;
   color: ${colors.GRAY_DARK};
   text-transform: uppercase;
   letter-spacing: 0.05em;
`;

const ContactList = styled.div`
   display: flex;
   flex-direction: column;
   gap: 4px;
`;

const ContactRow = styled.div`
   display: grid;
   grid-template-columns: 92px 1fr;
   gap: 8px;
   align-items: center;
`;

const ContactLbl = styled.span`
   font-size: 11px;
   color: ${colors.GRAY3};
   font-weight: 700;
`;

const ContactVal = styled.span`
   font-size: 13px;
   color: ${colors.MAIN_DARK};
   word-break: break-word;
`;

const ContactLink = styled.a`
   font-size: 13px;
   color: ${colors.PRIMARY};
   font-weight: 600;
   text-decoration: none;
   word-break: break-all;
   &:hover { text-decoration: underline; }
`;

const PaymentOptionsBlock = styled.div<{ $missing?: boolean }>`
   padding: 10px;
   background: ${({ $missing }) =>
      $missing ? `${colors.DANGER}08` : `${colors.PRIMARY}08`};
   border: 1px solid
      ${({ $missing }) =>
         $missing ? `${colors.DANGER}40` : `${colors.PRIMARY}30`};
   border-radius: 10px;
   display: flex;
   flex-direction: column;
   gap: 6px;
`;

const PaymentOptionsTitle = styled.p`
   font-size: 12px;
   font-weight: 800;
   color: ${colors.GRAY_DARK};
   text-transform: uppercase;
   letter-spacing: 0.05em;
`;

const PaymentOptionsEmpty = styled.p`
   font-size: 12px;
   color: ${colors.GRAY3};
   line-height: 1.4;
`;

const PaymentOptionsList = styled.div`
   display: flex;
   flex-direction: column;
   gap: 6px;
`;

const PaymentOptionRow = styled.div`
   display: grid;
   grid-template-columns: auto 1fr;
   gap: 10px;
   align-items: center;
   padding: 8px 10px;
   background: ${colors.WHITE};
   border: 1px solid ${colors.GRAY13};
   border-radius: 8px;
`;


const PaymentOptionInfo = styled.div`
   display: flex;
   flex-direction: column;
   gap: 1px;
   min-width: 0;
`;

const PaymentOptionName = styled.span`
   font-size: 12px;
   font-weight: 700;
   color: ${colors.GRAY_DARK};
`;

const PaymentOptionMono = styled.span`
   font-family: monospace;
   font-size: 12px;
   color: ${colors.GRAY1};
`;

const PaymentOptionSub = styled.span`
   font-size: 11px;
   color: ${colors.GRAY5};
`;

const BtnRow = styled.div`
   display: flex;
   justify-content: flex-end;
   align-items: stretch;
   gap: 10px;
`;

const SendInquiryBtn = styled.button`
   display: flex;
   flex-direction: column;
   align-items: center;
   justify-content: center;
   gap: 1px;
   min-width: 200px;
   padding: 6px 18px;
   background: ${colors.PRIMARY};
   color: white;
   border: none;
   border-radius: 8px;
   cursor: pointer;
   font-family: inherit;
   &:hover:not(:disabled) { opacity: 0.92; }
   &:disabled {
      background: ${colors.GRAY10};
      color: ${colors.GRAY1};
      cursor: not-allowed;
   }
`;

const SendInquiryMain = styled.span`
   font-size: 13px;
   font-weight: 800;
   line-height: 1.2;
`;

const SendInquirySub = styled.span`
   font-size: 11px;
   font-weight: 600;
   opacity: 0.85;
   line-height: 1.2;
`;

export default InquireCredits;
